import { Fragment } from 'preact/jsx-runtime';
import { isAddToCartEvent } from '@nespresso/catalog-components-react/controls/AddToCartButton';
import AccessoryProductCard from '../AccessoryProductCard/AccessoryProductCard.tsx';
import CoffeeProductCard from '../CoffeeProductCard/CoffeeProductCard.tsx';
import CardDrawers from './CardDrawers.tsx';
import { type PersistedState } from '../../Store.ts';
import {
  buildProductUrl,
  productTypeToUrlCompatibleString,
  type Product
} from '@/domain/Product.ts';
import useCardModalId from '../hooks/useCardModalId.tsx';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore.tsx';
import useRoute from '@/widgets/hooks/useRoute.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '@/widgets/ProductList/config/index.ts';
import { trackProductClick } from '../../Tracking.ts';
import useMarketConfig from '@/widgets/hooks/useMarketConfig.tsx';
import navigateToProductUrl from '@/widgets/utils/navigateToProductUrl.ts';
import { trackPlpInteraction } from '@/widgets/services/TrackingService.ts';
import MachineProductCard from '../MachineProductCard/MachineProductCard.tsx';

export type ProductTypeCardProps = {
  product: Product;
  lazyLoadProductImage?: boolean;
  trackingPosition: number;
  trackingList: string;
  onPurchaseSectionImpression: () => void;
  itemListName: string;
};

export default function ProductTypeCard({
  product,
  lazyLoadProductImage,
  trackingPosition,
  trackingList,
  onPurchaseSectionImpression,
  itemListName
}: ProductTypeCardProps) {
  const [, setState] = useQueryStringStore<PersistedState>();
  const { enableProductCardQuickView } = useWidgetOptions<Options>();
  const quickViewModalId = useCardModalId<PersistedState['openModal']>(
    'quick-view-',
    product.id
  );
  const route = useRoute();
  const {
    financialSettings: { defaultCurrency },
    discoveryOfferBundleProducts
  } = useMarketConfig();

  const { market, locale } = route;
  const { slug } = product;
  const url = buildProductUrl(
    market,
    locale,
    productTypeToUrlCompatibleString[product.type],
    product.technologies[0],
    slug
  );
  const { inFavorites } = useRoute();

  function handleProductCardClick(event: MouseEvent) {
    if (isAddToCartEvent(event)) {
      return;
    }

    if (enableProductCardQuickView) {
      trackPlpInteraction({ actionType: 'plp quick view', product });
      setState({ openModal: quickViewModalId.value });
    } else if (inFavorites) {
      trackProductClick(
        product,
        trackingPosition,
        discoveryOfferBundleProducts.includes(product.id),
        defaultCurrency,
        `wishlist - ${product.type}`,
        'wishlist',
        'wishlist_page',
        'wishlist_page'
      );

      navigateToProductUrl(event, url);
    } else {
      trackProductClick(
        product,
        trackingPosition,
        discoveryOfferBundleProducts.includes(product.id),
        defaultCurrency,
        trackingList
      );
      navigateToProductUrl(event, url);
    }
  }

  return (
    <Fragment key={product.id}>
      {product.type === 'capsule' && (
        <CoffeeProductCard
          product={product}
          onClick={(event) => handleProductCardClick(event)}
          lazyLoadProductImage={lazyLoadProductImage}
          onPurchaseSectionImpression={onPurchaseSectionImpression}
          itemListName={itemListName}
        />
      )}
      {product.type === 'machine' && (
        // TODO: implement the MachineProductCard, currently it's a duplication of the AccessoryProductCard
        <MachineProductCard
          product={product}
          onClick={(event) => handleProductCardClick(event)}
          lazyLoadProductImage={lazyLoadProductImage}
          onPurchaseSectionImpression={onPurchaseSectionImpression}
          itemListName={itemListName}
        />
      )}
      {product.type === 'accessory' && (
        <AccessoryProductCard
          product={product}
          onClick={(event) => handleProductCardClick(event)}
          lazyLoadProductImage={lazyLoadProductImage}
          onPurchaseSectionImpression={onPurchaseSectionImpression}
          itemListName={itemListName}
        />
      )}

      <CardDrawers
        product={product}
        productUrl={url}
        trackingPosition={trackingPosition}
        trackingList={trackingList}
      />
    </Fragment>
  );
}
